import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

/**
 * Eis 1 van de gerichte arbo-vrijstelling (Compliance Vetting Tiers, migratie 175):
 * de werkgever verklaart dat de inzet van BeLagom voortvloeit uit zijn arbo- of
 * preventiebeleid. Zonder verklaring zet de verzamelfactuur alle uren onder de
 * WKR-vrije ruimte. Alleen de hoofdgebruiker kan verklaren of intrekken.
 */
export const ArboBeleidVerklaring: React.FC = () => {
  const { t } = useTranslation('employer');
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const hoofdgebruiker = user?.role === 'opdrachtgever';

  const { data: profiel } = useQuery({
    queryKey: ['employer-profile'],
    queryFn: async () => (await api.get('/employer/profile')).data.data,
  });

  const mutatie = useMutation({
    mutationFn: async (verklaard: boolean) => (await api.post('/employer/arbo-beleid', { verklaard })).data,
    onSuccess: (_res, verklaard) => {
      toast.success(verklaard ? t('arboBeleid.opgeslagen') : t('arboBeleid.ingetrokken'));
      queryClient.invalidateQueries({ queryKey: ['employer-profile'] });
    },
    onError: (error: any) => toast.error(error.response?.data?.message || t('invoices.errorGeneric')),
  });

  if (!profiel) return null;
  const verklaardOp = profiel.arbo_beleid_verklaard_op ? new Date(profiel.arbo_beleid_verklaard_op) : null;

  if (verklaardOp) {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold text-emerald-900">{t('arboBeleid.actiefTitel')}</p>
          <p className="mt-1 text-emerald-800">
            {t('arboBeleid.actiefBody', { datum: verklaardOp.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) })}
          </p>
        </div>
        {hoofdgebruiker && (
          <button
            type="button"
            onClick={() => mutatie.mutate(false)}
            disabled={mutatie.isPending}
            className="text-xs font-medium text-emerald-800 underline hover:text-emerald-950"
          >
            {t('arboBeleid.intrekken')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold text-amber-900">{t('arboBeleid.ontbreektTitel')}</p>
          <p className="mt-1 text-amber-800">{t('arboBeleid.ontbreektBody')}</p>
          {hoofdgebruiker ? (
            <>
              <p className="mt-3 rounded-md bg-white/70 p-3 text-gray-800">{t('arboBeleid.verklaring')}</p>
              <button
                type="button"
                onClick={() => mutatie.mutate(true)}
                disabled={mutatie.isPending}
                className="mt-3 inline-flex items-center rounded-lg bg-[#0b1f4d] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#13306e] disabled:opacity-60"
              >
                {t('arboBeleid.akkoord')}
              </button>
            </>
          ) : (
            <p className="mt-2 text-amber-800">{t('arboBeleid.alleenHoofdgebruiker')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArboBeleidVerklaring;
